import dayjs from 'dayjs';

import type {
  DueExpense,
  Expense,
  ExpenseHead,
  RecurringExpense,
  SaveRecurringExpenseInput,
} from '../api/cash';
import type { PaymentMethod, RecurrenceFrequency } from '../generated/enums';
import { formatDate } from '../format/dates';
import { formatAmount } from '../format/money';
import { expenseAmount, type ExpenseDraft } from './expense';

/**
 * Recurring expenses (Cash & bank step 8): the template form's rules, its schedule in words, the due
 * list's words, and the voucher a due item pre-fills. The schedule itself — which days are due — is
 * the server's (`RecurrenceSchedule`); nothing here works one out, so the two can never disagree.
 */

export const frequencyLabels: Record<RecurrenceFrequency, string> = {
  Monthly: 'Monthly',
  Weekly: 'Weekly',
  Yearly: 'Yearly',
};

/** Sunday 0 to Saturday 6, as the server stores it. */
export const weekdayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** 1st, 2nd, 3rd, 4th … 11th, 12th, 13th … 21st, 22nd, 23rd … 31st. */
export function ordinal(day: number): string {
  const teen = day % 100 >= 11 && day % 100 <= 13;
  const suffix = teen
    ? 'th'
    : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] ?? 'th');

  return `${day}${suffix}`;
}

/** "Every month on the 5th", "Every Monday", "Every year on 15 April". */
export function scheduleWords(
  template: Pick<RecurringExpense, 'frequency' | 'dayOfMonth' | 'dayOfWeek' | 'monthOfYear'>,
): string {
  switch (template.frequency) {
    case 'Weekly':
      return template.dayOfWeek === null
        ? 'Every week'
        : `Every ${weekdayNames[template.dayOfWeek] ?? 'week'}`;
    case 'Yearly':
      return template.monthOfYear === null || template.dayOfMonth === null
        ? 'Every year'
        : `Every year on ${template.dayOfMonth} ${monthNames[template.monthOfYear - 1] ?? ''}`.trim();
    case 'Monthly':
      return template.dayOfMonth === null
        ? 'Every month'
        : `Every month on the ${ordinal(template.dayOfMonth)}${template.dayOfMonth > 28 ? ' (or the last day)' : ''}`;
  }
}

/** "Due today", "Due in 3 days", "5 days late" — how pressing a due item is. */
export function dueWords(
  dueOn: string,
  today: string,
): { text: string; tone: 'muted' | 'warning' } {
  const days = dayjs(dueOn.slice(0, 10)).diff(dayjs(today), 'day');

  if (days === 0) {
    return { text: 'Due today', tone: 'warning' };
  }

  if (days > 0) {
    return { text: days === 1 ? 'Due tomorrow' : `Due in ${days} days`, tone: 'muted' };
  }

  return { text: -days === 1 ? '1 day late' : `${-days} days late`, tone: 'warning' };
}

/** "Shop rent · ₹15,000 · due 5 Oct" — one line for a list. */
export function dueLine(due: DueExpense, currency = ''): string {
  return `${due.name} · ${currency}${formatAmount(due.typicalAmount)} · due ${dayjs(due.dueOn).format('D MMM')}`;
}

/**
 * The voucher a due item opens with: its head, typical amount, method and account, paid today
 * (a week-ahead item is paid today too — no voucher is dated in the future), and the link that
 * settles the occurrence. The amount stays changeable (owner, 2026-10-23).
 */
export function expenseDraftFromDue(due: DueExpense, today: string): ExpenseDraft {
  return {
    headId: due.expenseHeadId,
    amount: String(due.typicalAmount),
    paidOn: today,
    method: due.method,
    reference: due.notes ?? '',
    cashAccountId: due.cashAccountId,
    recurrence: { recurringExpenseId: due.recurringExpenseId, dueOn: due.dueOn.slice(0, 10) },
  };
}

/**
 * The morning screen's line — "2 to record · Shop rent, 18 days late" — or null when nothing is due.
 * Without it a clerk learnt rent was due only by opening Expenses (2026-10-23). Leads with the most
 * pressing, which the due list already sorts first.
 */
export function dueSummary(
  due: readonly DueExpense[],
  today: string,
): { text: string; tone: 'muted' | 'warning' } | null {
  const first = due[0];

  if (!first) {
    return null;
  }

  const words = dueWords(first.dueOn, today);
  const late = due.some((item) => item.dueOn.slice(0, 10) <= today);

  return {
    text: `${due.length} to record · ${first.name}, ${words.text.charAt(0).toLowerCase()}${words.text.slice(1)}`,
    tone: late ? 'warning' : 'muted',
  };
}

/**
 * Which occurrence a voucher paid — "For 05-09-2026" — or null for an ordinary voucher. Without it a
 * register cannot tell September's rent from October's (2026-10-23).
 */
export function occurrenceWords(expense: Pick<Expense, 'recurrenceDueOn'>): string | null {
  return expense.recurrenceDueOn ? `For the one due ${formatDate(expense.recurrenceDueOn)}` : null;
}

/* --------------------------------------------------------------- the template form */

export interface RecurringDraft {
  name: string;
  headId: number | null;
  /** As typed. */
  amount: string;
  method: PaymentMethod;
  frequency: RecurrenceFrequency;
  /** As typed, 1–31, for monthly and yearly. Starts empty: a choice, never today's date by default. */
  dayOfMonth: string;
  /** Sunday 0 to Saturday 6; null until chosen. */
  dayOfWeek: number | null;
  /** 1–12; null until chosen. */
  monthOfYear: number | null;
  startsOn: string;
  cashAccountId: number | null;
  notes: string;
  isActive: boolean;
}

export function emptyRecurring(today: string): RecurringDraft {
  return {
    name: '',
    headId: null,
    amount: '',
    method: 'BankTransfer',
    frequency: 'Monthly',
    // Empty, not today's: a new user who set rent up on the 23rd got "rent on the 23rd" without
    // noticing (2026-10-23). The head starts empty for the same reason.
    dayOfMonth: '',
    dayOfWeek: null,
    monthOfYear: null,
    startsOn: today,
    cashAccountId: null,
    notes: '',
    isActive: true,
  };
}

/** A template back into the form, for editing. */
export function draftOf(template: RecurringExpense): RecurringDraft {
  return {
    name: template.name,
    headId: template.expenseHeadId,
    amount: String(template.typicalAmount),
    method: template.method,
    frequency: template.frequency,
    dayOfMonth: template.dayOfMonth === null ? '' : String(template.dayOfMonth),
    dayOfWeek: template.dayOfWeek,
    monthOfYear: template.monthOfYear,
    startsOn: template.startsOn.slice(0, 10),
    cashAccountId: template.cashAccountId,
    notes: template.notes ?? '',
    isActive: template.isActive,
  };
}

function dayOf(typed: string): number | null {
  const trimmed = typed.trim();

  return /^\d{1,2}$/.test(trimmed) && Number(trimmed) >= 1 && Number(trimmed) <= 31
    ? Number(trimmed)
    : null;
}

/** The one sentence standing between the form and Save, in the server's order, or null. */
export function recurringProblem(
  draft: RecurringDraft,
  heads: readonly ExpenseHead[],
): string | null {
  if (draft.name.trim() === '') {
    return 'Give it a name — "Shop rent", "Electricity".';
  }

  const amount = expenseAmount(draft.amount);

  if (amount === null) {
    return draft.amount.trim() === ''
      ? 'Enter the typical amount.'
      : 'The amount must be more than 0, in rupees and paise.';
  }

  if (draft.method === 'Adjustment') {
    return 'An adjustment moves no money, so it cannot be how an expense is paid.';
  }

  if (draft.startsOn === '') {
    return 'Say from which day it falls due.';
  }

  if (draft.frequency === 'Weekly' && draft.dayOfWeek === null) {
    return 'Say which day of the week.';
  }

  if (draft.frequency === 'Yearly' && draft.monthOfYear === null) {
    return 'Say which month.';
  }

  if (draft.frequency !== 'Weekly' && dayOf(draft.dayOfMonth) === null) {
    return 'Say which day of the month, 1 to 31.';
  }

  if (draft.headId === null) {
    return 'Choose what it is filed under.';
  }

  if (!heads.some((head) => head.id === draft.headId && head.isActive)) {
    return 'That expense head is retired. Choose another.';
  }

  return null;
}

/**
 * Whether the form's schedule differs from what was saved — then the saved "next due" is stale and
 * is not shown; the server works the new one out on save (2026-10-23: the page read "on the 10th"
 * above "Next due 5 Nov").
 */
export function scheduleChanged(draft: RecurringDraft, template: RecurringExpense): boolean {
  const saved = draftOf(template);

  return (
    draft.frequency !== saved.frequency ||
    draft.startsOn !== saved.startsOn ||
    (draft.frequency !== 'Weekly' && dayOf(draft.dayOfMonth) !== dayOf(saved.dayOfMonth)) ||
    (draft.frequency === 'Weekly' && draft.dayOfWeek !== saved.dayOfWeek) ||
    (draft.frequency === 'Yearly' && draft.monthOfYear !== saved.monthOfYear)
  );
}

/** The request, or null while there is a problem. Only the frequency's own day fields are sent. */
export function recurringRequest(
  draft: RecurringDraft,
  heads: readonly ExpenseHead[],
): SaveRecurringExpenseInput | null {
  const amount = expenseAmount(draft.amount);

  if (recurringProblem(draft, heads) !== null || amount === null || draft.headId === null) {
    return null;
  }

  return {
    name: draft.name.trim(),
    expenseHeadId: draft.headId,
    typicalAmount: amount,
    method: draft.method,
    frequency: draft.frequency,
    startsOn: draft.startsOn,
    dayOfMonth: draft.frequency === 'Weekly' ? null : dayOf(draft.dayOfMonth),
    dayOfWeek: draft.frequency === 'Weekly' ? draft.dayOfWeek : null,
    monthOfYear: draft.frequency === 'Yearly' ? draft.monthOfYear : null,
    cashAccountId: draft.cashAccountId,
    notes: draft.notes.trim() || null,
    isActive: draft.isActive,
  };
}
